import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

interface User {
  id: number;
  email: string;
  username: string;
  role_id: number;
  role_name?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(
    localStorage.getItem("access_token")
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isValidating, setIsValidating] = useState(false);

  const isAuthenticated = !!user && !!token;

  const validateToken = async (tokenToValidate?: string) => {
    const tokenToUse = tokenToValidate || token;
    if (isValidating || !tokenToUse) return;

    setIsValidating(true);
    setIsLoading(true);
    try {
      const apiUrl =
        process.env.REACT_APP_API_URL || "http://localhost:4001/api/v1";
      const response = await fetch(`${apiUrl}/auth/profile`, {
        headers: {
          Authorization: `Bearer ${tokenToUse}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.data);
      } else {
        setUser(null);
        setToken(null);
        localStorage.removeItem("access_token");
      }
    } catch (error) {
      setUser(null);
      setToken(null);
      localStorage.removeItem("access_token");
    } finally {
      setIsLoading(false);
      setIsValidating(false);
    }
  };

  useEffect(() => {
    const storedToken = localStorage.getItem("access_token");
    if (storedToken) {
      setToken(storedToken);
      validateToken(storedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      // Call login API
      const apiUrl =
        process.env.REACT_APP_API_URL || "http://localhost:4001/api/v1";
      const response = await fetch(`${apiUrl}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();
        setToken(data.data.tokens.access_token);
        setUser(data.data.user);
        localStorage.setItem("access_token", data.data.tokens.access_token);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Login failed");
      }
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setIsValidating(false);
    setUser(null);
    setToken(null);
    localStorage.removeItem("access_token");
  };

  const value = {
    user,
    token,
    login,
    logout,
    isLoading,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
